import React, { useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import './Navbar.css';

interface NavItem {
  href?: string;
  to?: string;
  label: string;
}

interface NavLink extends NavItem {
  isButton?: boolean;
  children?: NavItem[];
}

interface NavbarProps {
  logo?: {
    src?: string;
    text: string;
  };
  links: NavLink[];
  overlay?: boolean;
  overlayUntil?: string;
}

const MOBILE_NAV_QUERY = '(max-width: 768px)';

const findOverlayUntil = (selector?: string) => {
  if (!selector) return null;
  return document.querySelector(selector);
};

const isMobileNav = () =>
  typeof window !== 'undefined' && window.matchMedia(MOBILE_NAV_QUERY).matches;

const NavAnchor: React.FC<NavItem & { className?: string; onClick?: () => void }> = ({
  href,
  to,
  label,
  className,
  onClick,
}) => {
  if (to) {
    return (
      <Link to={to} className={className} onClick={onClick}>
        {label}
      </Link>
    );
  }
  return (
    <a href={href} className={className} onClick={onClick}>
      {label}
    </a>
  );
};

const Navbar: React.FC<NavbarProps> = ({
  logo = { text: 'Orca Labs' },
  links,
  overlay = false,
  overlayUntil,
}) => {
  const navRef = useRef<HTMLElement>(null);
  const leaveTimer = useRef<number | undefined>(undefined);
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(isMobileNav);

  const openDropdown = (label: string) => {
    window.clearTimeout(leaveTimer.current);
    setOpenMenu(label);
  };

  const closeDropdown = () => {
    window.clearTimeout(leaveTimer.current);
    leaveTimer.current = window.setTimeout(() => setOpenMenu(null), 120);
  };

  const closeMenus = () => {
    window.clearTimeout(leaveTimer.current);
    setOpenMenu(null);
    setMobileOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileOpen((open) => {
      if (open) setOpenMenu(null);
      return !open;
    });
  };

  useEffect(() => {
    if (!overlay || !overlayUntil) return undefined;

    const updateOverlay = () => {
      const nav = navRef.current;
      const until = findOverlayUntil(overlayUntil);
      if (!nav || !until) {
        setScrolled(false);
        return;
      }

      setScrolled(nav.getBoundingClientRect().bottom >= until.getBoundingClientRect().top);
    };

    updateOverlay();
    window.addEventListener('scroll', updateOverlay, { passive: true });
    window.addEventListener('resize', updateOverlay);
    return () => {
      window.removeEventListener('scroll', updateOverlay);
      window.removeEventListener('resize', updateOverlay);
    };
  }, [overlay, overlayUntil]);

  useEffect(() => {
    const media = window.matchMedia(MOBILE_NAV_QUERY);
    const onChange = () => {
      setIsMobile(media.matches);
      if (!media.matches) closeMenus();
    };
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!openMenu && !mobileOpen) return undefined;
    const close = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        closeMenus();
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenus();
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [openMenu, mobileOpen]);

  const className = [
    'consulting-navbar',
    overlay ? 'consulting-navbar--overlay' : '',
    overlay && scrolled ? 'is-scrolled' : '',
    mobileOpen ? 'is-menu-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <nav ref={navRef} className={className}>
      <div className="nav-container">
        <Link to="/" className="logo">
          {logo.src && <img src={logo.src} alt={logo.text} height="40" width="40" />}
          <span className="logo-text">{logo.text}</span>
        </Link>
        <button
          type="button"
          className="nav-menu-toggle"
          aria-expanded={mobileOpen}
          aria-controls="consulting-nav-links"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          onClick={toggleMobileMenu}
        >
          <span className="nav-menu-toggle__arrow" aria-hidden="true" />
        </button>
        <div
          id="consulting-nav-links"
          className={`nav-links${mobileOpen ? ' is-open' : ''}`}
          inert={isMobile && !mobileOpen ? true : undefined}
        >
          <div className="nav-links__panel">
            {links.map((link) => {
              if (link.isButton) {
                return link.to ? (
                  <Button
                    key={link.label}
                    component={Link}
                    to={link.to}
                    variant="contained"
                    onClick={closeMenus}
                  >
                    {link.label}
                  </Button>
                ) : (
                  <Button
                    key={link.label}
                    component="a"
                    href={link.href}
                    variant="contained"
                    onClick={closeMenus}
                  >
                    {link.label}
                  </Button>
                );
              }

              if (link.children?.length) {
                const expanded = openMenu === link.label;
                return (
                  <div
                    key={link.label}
                    className={`nav-dropdown${expanded ? ' is-open' : ''}`}
                    onMouseEnter={() => {
                      if (!isMobile) openDropdown(link.label);
                    }}
                    onMouseLeave={() => {
                      if (!isMobile) closeDropdown();
                    }}
                  >
                    <button
                      type="button"
                      className="nav-dropdown__trigger"
                      aria-expanded={expanded}
                      aria-haspopup="true"
                      onClick={() => setOpenMenu(expanded ? null : link.label)}
                    >
                      {link.label}
                      <span className="nav-dropdown__chevron" aria-hidden="true" />
                    </button>
                    <div
                      className="nav-dropdown__menu"
                      role="menu"
                      inert={isMobile && !expanded ? true : undefined}
                    >
                      <div className="nav-dropdown__panel">
                        {link.children.map((child) => (
                          <NavAnchor
                            key={child.label}
                            {...child}
                            className="nav-dropdown__item"
                            onClick={closeMenus}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              return <NavAnchor key={link.label} {...link} onClick={closeMenus} />;
            })}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
